"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { L } from "@/extensions/lida/lida";
import { T } from "@/lib/i18n/T";
import type { LocaleCode } from "@/lib/i18n/locales";
import {
  createOrder,
  formatMoney,
  getCart,
  getShippingMethods,
  quote as quoteApi,
} from "@/lib/checkout/api";
import type {
  CartItem,
  CheckoutAddress,
  Contact,
  PaymentRail,
  Quote,
  ShippingMethod,
} from "@/lib/checkout/types";
import { OrderSummary } from "./OrderSummary";

const COUNTRIES = [
  "Italy", "Russia", "United States", "United Kingdom", "Spain", "Germany",
  "France", "Japan", "South Korea", "Australia", "Brazil", "Türkiye",
];

const STEPS = [
  "checkout.steps.contact",
  "checkout.steps.shipping",
  "checkout.steps.delivery",
  "checkout.steps.payment",
];

const RAILS: { id: PaymentRail; labelId: string; descId: string; available: boolean }[] = [
  { id: "paypal", labelId: "checkout.rails.paypal.label", descId: "checkout.rails.paypal.desc", available: true },
  { id: "crypto", labelId: "checkout.rails.crypto.label", descId: "checkout.rails.crypto.desc", available: false },
  { id: "ru", labelId: "checkout.rails.ru.label", descId: "checkout.rails.ru.desc", available: false },
];

const isEmail = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());

export function CheckoutFlow({ locale }: { locale: LocaleCode }) {
  const href = (p: string) => `/${locale}${p}`;

  const [items, setItems] = useState<CartItem[]>([]);
  const [methods, setMethods] = useState<ShippingMethod[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [quote, setQuote] = useState<Quote | null>(null);

  const [step, setStep] = useState(0);
  const [contact, setContact] = useState<Contact>({ email: "", phone: "" });
  const [address, setAddress] = useState<CheckoutAddress>({
    recipient: "", street: "", city: "", postal: "", country: "Italy",
  });
  const [shippingMethodId, setShippingMethodId] = useState("");
  const [discountCode, setDiscountCode] = useState<string | null>(null);
  const [rail, setRail] = useState<PaymentRail>("paypal");
  const [placing, setPlacing] = useState(false);
  const [placedId, setPlacedId] = useState<string | null>(null);

  // Load the bag + shipping options once.
  useEffect(() => {
    let active = true;
    Promise.all([getCart(), getShippingMethods(address.country)]).then(([cart, ms]) => {
      if (!active) return;
      setItems(cart);
      setMethods(ms);
      setShippingMethodId((cur) => cur || ms[0]?.id || "");
      setLoaded(true);
    });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  // Re-quote whenever something price-affecting changes (server is authoritative).
  useEffect(() => {
    if (!items.length) return;
    let active = true;
    quoteApi({ items, shippingMethodId, discountCode }).then((q) => {
      if (active) setQuote(q);
    });
    return () => { active = false; };
  }, [items, shippingMethodId, discountCode]);

  const setAddr = (patch: Partial<CheckoutAddress>) => setAddress((a) => ({ ...a, ...patch }));

  const canContinue = (s: number): boolean => {
    if (s === 0) return isEmail(contact.email);
    if (s === 1)
      return Boolean(address.recipient && address.street && address.city && address.postal && address.country);
    if (s === 2) return Boolean(shippingMethodId);
    return true;
  };

  const place = async () => {
    if (!shippingMethodId) return;
    setPlacing(true);
    const res = await createOrder({ contact, address, shippingMethodId, discountCode, rail });
    // TODO(backend): res.approvalUrl → redirect (web) or open in Capacitor Browser (app).
    setPlacing(false);
    setPlacedId(res.orderId);
  };

  // ---- Empty / loading / placed states ----
  if (loaded && items.length === 0) {
    return (
      <div className="rb-checkout-empty csx3-C11 csx3-g-16">
        <h2><T id="cart.empty" /></h2>
        <Link href={href("")} className="rb-btn rb-btn--primary"><T id="common.continueShopping" /></Link>
      </div>
    );
  }

  if (placedId) {
    return (
      <div className="rb-checkout-done csx3-C11 csx3-g-16">
        <div className="rb-checkout-done-ico csx3-R11" aria-hidden="true">
          <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
        </div>
        <h2><T id="checkout.done.title" /></h2>
        <p className="rb-checkout-done-note">
          <T id="checkout.done.demo" />
        </p>
        <span className="rb-checkout-done-id">{placedId}</span>
        <Link href={href("/dashboard?s=orders")} className="rb-btn rb-btn--ghost"><T id="checkout.done.viewOrders" /></Link>
      </div>
    );
  }

  return (
    <div className="rb-checkout csx3-R00 csx3-fw csx3-g-32">
      {/* ---- Steps ---- */}
      <div className="rb-co-main csx3-f1 csx3-C00 csx3-g-24">
        <ol className="rb-co-steps csx3-R01 csx3-fw csx3-g-8">
          {STEPS.map((stepId, i) => (
            <li key={stepId} className="csx3-R01 csx3-g-8">
              <button
                type="button"
                className={`rb-co-step ${i === step ? "is-active" : ""} ${i < step ? "is-done" : ""}`}
                onClick={() => i < step && setStep(i)}
                disabled={i > step}
              >
                <span className="rb-co-step-n">{i < step ? "✓" : i + 1}</span>
                <T id={stepId} />
              </button>
              {i < STEPS.length - 1 && <span className="rb-co-step-sep" aria-hidden="true" />}
            </li>
          ))}
        </ol>

        {/* Step 0 — Contact */}
        {step === 0 && (
          <section className="rb-co-panel csx3-C00 csx3-w-full csx3-g-16">
            <label className="rb-field csx3-C00 csx3-g-6">
              <span className="rb-field-label"><T id="common.email" /></span>
              <input className="rb-field-control" type="email" autoComplete="email" placeholder="you@example.com"
                value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} />
            </label>
            <label className="rb-field csx3-C00 csx3-g-6">
              <span className="rb-field-label"><T id="checkout.contact.phoneOptional" /></span>
              <input className="rb-field-control" type="tel" autoComplete="tel" placeholder="+39 123 456 7890"
                value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} />
            </label>
            <p className="rb-co-hint"><T id="checkout.contact.note" /></p>
          </section>
        )}

        {/* Step 1 — Shipping address */}
        {step === 1 && (
          <section className="rb-co-panel csx3-C00 csx3-w-full csx3-g-12">
            <label className="rb-field csx3-C00 csx3-g-6">
              <span className="rb-field-label"><T id="checkout.address.recipient" /></span>
              <input className="rb-field-control" type="text" autoComplete="name" placeholder="Jane Doe"
                value={address.recipient} onChange={(e) => setAddr({ recipient: e.target.value })} />
            </label>
            <label className="rb-field csx3-C00 csx3-g-6">
              <span className="rb-field-label"><T id="checkout.address.street" /></span>
              <input className="rb-field-control" type="text" autoComplete="street-address" placeholder="Via Roma 1"
                value={address.street} onChange={(e) => setAddr({ street: e.target.value })} />
            </label>
            <div className="rb-co-grid2">
              <label className="rb-field csx3-C00 csx3-g-6">
                <span className="rb-field-label"><T id="checkout.address.city" /></span>
                <input className="rb-field-control" type="text" autoComplete="address-level2"
                  value={address.city} onChange={(e) => setAddr({ city: e.target.value })} />
              </label>
              <label className="rb-field csx3-C00 csx3-g-6">
                <span className="rb-field-label"><T id="checkout.address.postalCode" /></span>
                <input className="rb-field-control" type="text" autoComplete="postal-code"
                  value={address.postal} onChange={(e) => setAddr({ postal: e.target.value })} />
              </label>
            </div>
            <label className="rb-field csx3-C00 csx3-g-6">
              <span className="rb-field-label"><T id="checkout.address.country" /></span>
              <select className="rb-field-control" value={address.country} onChange={(e) => setAddr({ country: e.target.value })}>
                {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
          </section>
        )}

        {/* Step 2 — Delivery method */}
        {step === 2 && (
          <section className="rb-co-panel csx3-C00 csx3-w-full csx3-g-8">
            {methods.map((m) => (
              <button
                key={m.id}
                type="button"
                className={`rb-co-option csx3-Rs1 csx3-w-full ${shippingMethodId === m.id ? "is-selected" : ""}`}
                onClick={() => setShippingMethodId(m.id)}
              >
                <span className="csx3-C00 csx3-g-4">
                  {/* Backend-provided copy (see lib/checkout/api.ts): no catalog
                      can hold it, so it stays on WorldLida's runtime translation. */}
                  <span className="rb-co-option-title"><L text={m.label} /></span>
                  <span className="rb-co-option-sub"><L text={m.eta} /></span>
                </span>
                <span className="rb-co-option-price">{m.price === 0 ? "Free" : formatMoney(m.price)}</span>
              </button>
            ))}
          </section>
        )}

        {/* Step 3 — Payment */}
        {step === 3 && (
          <section className="rb-co-panel csx3-C00 csx3-w-full csx3-g-16">
            <div className="rb-co-review csx3-C00 csx3-w-full csx3-g-4">
              <span className="rb-co-review-line"><strong><T id="checkout.steps.shipTo" /></strong> {address.recipient}, {address.street}, {address.city} {address.postal}, {address.country}</span>
              <span className="rb-co-review-line"><strong><T id="checkout.steps.contact" /></strong> {contact.email}</span>
            </div>
            <div className="rb-co-rails csx3-C00 csx3-w-full csx3-g-8">
              {RAILS.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  className={`rb-co-option csx3-Rs1 csx3-w-full ${rail === r.id ? "is-selected" : ""} ${r.available ? "" : "is-soon"}`}
                  onClick={() => r.available && setRail(r.id)}
                  disabled={!r.available}
                >
                  <span className="csx3-C00 csx3-g-4">
                    <span className="rb-co-option-title"><T id={r.labelId} /></span>
                    <span className="rb-co-option-sub"><T id={r.descId} /></span>
                  </span>
                  {!r.available && <span className="rb-co-soon"><T id="common.comingSoon" /></span>}
                </button>
              ))}
            </div>
          </section>
        )}

        {/* ---- Nav ---- */}
        <div className="rb-co-nav csx3-Rs1 csx3-w-full">
          {step > 0 ? (
            <button type="button" className="rb-btn rb-btn--ghost" onClick={() => setStep(step - 1)}>
              <T id="common.back" />
            </button>
          ) : (
            <Link href={href("/cart")} className="rb-btn rb-btn--ghost"><T id="checkout.backToCart" /></Link>
          )}

          {step < STEPS.length - 1 ? (
            <button type="button" className="rb-btn rb-btn--primary" disabled={!canContinue(step)} onClick={() => setStep(step + 1)}>
              <T id="common.continue" />
            </button>
          ) : (
            <button type="button" className="rb-btn rb-btn--primary" disabled={placing} onClick={place}>
              {placing ? <T id="checkout.processing" /> : <><T id="checkout.pay" />{quote ? ` ${formatMoney(quote.total, quote.currency)}` : ""}</>}
            </button>
          )}
        </div>
      </div>

      {/* ---- Summary ---- */}
      <OrderSummary
        quote={quote}
        onApplyDiscount={(c) => setDiscountCode(c)}
        onRemoveDiscount={() => setDiscountCode(null)}
      />
    </div>
  );
}
